/**
 * useReadReceipts — the counterpart's `last_read_at` for one conversation.
 *
 * THE HALF THAT DID NOT EXIST
 *
 * `message_reads` already carried everyone's read cursor and was already in the
 * realtime publication, but the only SELECT policy was "own row": realtime
 * respects RLS, so the other participant's UPDATE never reached the sender and
 * a read receipt could not be rendered. The policy that opens it to the pair
 * ships in `20260927150000_los_recibos_de_lectura_son_del_par.sql`; this hook
 * is the client half — one fetch for the current value, one subscription for
 * every change after it.
 *
 * A missing receipt is NOT an error state: it means "not read yet", and the
 * bubble keeps its single tick. Nothing here is allowed to surface an error —
 * a failed read state must never break the thread.
 */
import { useEffect, useState } from 'react'
import { supabase } from '../supabase'
import { messagingService } from '../services/messaging.service'

export function useReadReceipts(
  conversationId: string | undefined,
  otherUserId: string | null | undefined,
): string | null {
  const [theirLastReadAt, setTheirLastReadAt] = useState<string | null>(null)

  useEffect(() => {
    if (!conversationId || !otherUserId) {
      setTheirLastReadAt(null)
      return
    }

    let cancelled = false
    let hasSubscribedOnce = false
    setTheirLastReadAt(null)

    void messagingService
      .getConversationReadReceipts(conversationId)
      .then((rows) => {
        if (cancelled) return
        const theirs = rows.find((row) => row.profileId === otherUserId)
        setTheirLastReadAt(theirs?.lastReadAt ?? null)
      })
      .catch(() => {
        // Read state is decoration on top of a delivered message. Swallow it.
      })

    const channel = supabase
      .channel(`conv:${conversationId}:reads`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'message_reads',
          filter: `conversation_id=eq.${conversationId}`,
        },
        (payload) => {
          const row = (payload.new ?? null) as Record<string, unknown> | null
          if (!row) return
          // The caller's own row is also delivered by this channel; only the
          // counterpart's cursor moves the ticks.
          if (row.profile_id !== otherUserId) return
          const at = row.last_read_at as string | undefined
          if (at) setTheirLastReadAt(at)
        },
      )
      .subscribe((status) => {
        if (status !== 'SUBSCRIBED') return
        // Same reconnect gap as the messages channel: a socket that dropped
        // missed UPDATEs, and the receipt has to be re-read, not assumed.
        if (hasSubscribedOnce) {
          void messagingService
            .getConversationReadReceipts(conversationId)
            .then((rows) => {
              if (cancelled) return
              const theirs = rows.find((row) => row.profileId === otherUserId)
              setTheirLastReadAt(theirs?.lastReadAt ?? null)
            })
            .catch(() => {})
        }
        hasSubscribedOnce = true
      })

    return () => {
      cancelled = true
      void supabase.removeChannel(channel)
    }
  }, [conversationId, otherUserId])

  return theirLastReadAt
}
